import React, { useEffect, useState } from 'react';
import ReservationModal from './ReservationModal';
import { API_URL, fetchOpenEvents, formatRWF } from '../api';
import { useI18n } from '../i18n/LanguageContext';

// Community photos rotated across the pilgrimage cards (and the booking modal).
const CARD_IMAGES = [
  `${process.env.PUBLIC_URL}/assets/community/praise-and-worship.jpg`,
  `${process.env.PUBLIC_URL}/assets/community/candle-vigil.jpg`,
  `${process.env.PUBLIC_URL}/assets/community/candles-with-priest.jpg`,
];

// Shown when no pilgrimage is open for booking yet (or the API is down).
const SHOWCASE = [
  {
    id: 'kibeho-lady-visit',
    title: 'Kibeho Lady Visit',
    image: CARD_IMAGES[0],
    price: '10,000 RWF',
    dateIso: '2027-06-27',
    location: 'Kibeho',
    descriptionKey: 'pilgrimages.showcase.kibeho',
  },
  {
    id: 'mount-sinai-trek',
    title: 'Mount Sinai Trek',
    image: CARD_IMAGES[1],
    price: '10,000 RWF',
    dateIso: '2027-08-12',
    location: 'Sinai Peninsula',
    descriptionKey: 'pilgrimages.showcase.sinai',
  },
  {
    id: 'camino-de-santiago',
    title: 'Camino de Santiago Adventure',
    image: CARD_IMAGES[2],
    price: '10,000 RWF',
    dateIso: '2027-09-05',
    location: 'Spain',
    descriptionKey: 'pilgrimages.showcase.camino',
  },
];

function CalendarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21s-7-6.5-7-12a7 7 0 0 1 14 0c0 5.5-7 12-7 12z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function toCard(event, index) {
  return {
    id: event.id,
    title: event.name,
    image: CARD_IMAGES[index % CARD_IMAGES.length],
    price: formatRWF(event.price),
    dateIso: event.eventDate,
    location: event.destination,
    description: event.description || '',
    bookable: event.bookingOpen !== false,
    closed: event.bookingOpen === false,
    closesAt: event.bookingClosesAt,
    event,
  };
}

export default function RecentPilgrimages() {
  const { t, formatDate, formatDeadline } = useI18n();
  const [selected, setSelected] = useState(null);
  const [pilgrimages, setPilgrimages] = useState(API_URL ? [] : SHOWCASE);
  const [loading, setLoading] = useState(Boolean(API_URL));

  useEffect(() => {
    if (!API_URL) return undefined;
    let cancelled = false;
    fetchOpenEvents()
      .then((events) => {
        if (cancelled) return;
        setPilgrimages(events.length ? events.map(toCard) : SHOWCASE);
      })
      .catch(() => { if (!cancelled) setPilgrimages(SHOWCASE); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const wanted = e?.detail?.eventId;
      const first = (wanted && pilgrimages.find((p) => p.id === wanted && p.bookable))
        || [...pilgrimages].filter((p) => p.bookable)
          .sort((a, b) => String(a.event?.eventDate || '').localeCompare(String(b.event?.eventDate || '')))[0];
      if (first) setSelected(first);
      else document.getElementById('pilgrimages')?.scrollIntoView({ behavior: 'smooth' });
    };
    window.addEventListener('izm:open-reservation', handler);
    return () => window.removeEventListener('izm:open-reservation', handler);
  }, [pilgrimages]);

  return (
    <section id="pilgrimages" className="recent">
      <div className="recent-inner">
        <h2 className="recent-title">{t('pilgrimages.title')}</h2>

        {loading && <p className="recent-status">{t('pilgrimages.loading')}</p>}

        <div className="pilgrimage-grid">
          {pilgrimages.map((p) => (
            <article key={p.id} className="pilgrimage-card">
              <div className="pilgrimage-media">
                <img src={p.image} alt={p.title} />
                <span className="pilgrimage-price">{p.price}</span>
              </div>
              <div className="pilgrimage-body">
                <h3 className="pilgrimage-title">{p.title}</h3>
                <div className="pilgrimage-meta">
                  <span className="pilgrimage-meta-item">
                    <CalendarIcon />
                    {formatDate(p.dateIso)}
                  </span>
                  <span className="pilgrimage-meta-item">
                    <PinIcon />
                    {p.location}
                  </span>
                </div>
                <p className="pilgrimage-desc">{p.descriptionKey ? t(p.descriptionKey) : p.description}</p>
                {p.closesAt && (
                  <div className={`pilgrimage-deadline ${p.closed ? 'is-closed' : ''}`}>
                    {p.closed ? t('pilgrimages.closed') : t('pilgrimages.closes', { when: formatDeadline(p.closesAt) })}
                  </div>
                )}
                <button
                  type="button"
                  className="pilgrimage-cta"
                  onClick={() => setSelected(p)}
                  disabled={!p.bookable}
                >
                  {p.bookable ? t('pilgrimages.reserve') : p.closed ? t('pilgrimages.closed') : t('pilgrimages.opens_soon')}
                </button>
              </div>
            </article>
          ))}
        </div>

        <a href="/pilgrimages" className="recent-more">
          {t('pilgrimages.more')} <ChevronRight />
        </a>
      </div>

      {selected && (
        <ReservationModal
          pilgrimage={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
}
